import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * LeftRail
 * --------
 * Collapsible left rail shell. Each page is expected to inject its own contextual
 * controls into the rail via the `LeftRailProvider` context (see `useLeftRail`).
 *
 * Width:
 *   - Open: 280px
 *   - Collapsed: 40px (a thin strip with an expand button)
 *
 * Hidden on routes: `/login`, `/profile`, `/admin`
 *
 * Persistence: open/collapsed state is stored in `localStorage` under
 *   key `mvkg.leftRail.open` (string `'1'` or `'0'`).
 *
 * Usage from a page:
 *   const { setRailContent } = useLeftRail();
 *   useEffect(() => {
 *     setRailContent(<MyFilters/>);
 *     return () => setRailContent(null);
 *   }, [setRailContent]);
 */

export const LEFT_RAIL_WIDTH_OPEN = 280;
export const LEFT_RAIL_WIDTH_COLLAPSED = 40;
const HIDDEN_ROUTES = ['/login', '/profile', '/admin'];
const STORAGE_KEY = 'mvkg.leftRail.open';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const LeftRailContext = createContext({
  railContent: null,
  setRailContent: () => {},
  isOpen: false,
  toggle: () => {},
});

export function LeftRailProvider({ children }) {
  const [railContent, setRailContent] = useState(null);
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // default: collapsed
      return stored === '1';
    } catch (_) {
      return false;
    }
  });

  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch (_) {
        /* ignore */
      }
      return next;
    });
  };

  const value = useMemo(
    () => ({ railContent, setRailContent, isOpen, toggle }),
    [railContent, isOpen]
  );

  return (
    <LeftRailContext.Provider value={value}>{children}</LeftRailContext.Provider>
  );
}

export function useLeftRail() {
  return useContext(LeftRailContext);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LeftRail({ route, children }) {
  const { railContent, isOpen, toggle } = useLeftRail();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Hide entirely on certain routes
  if (route && HIDDEN_ROUTES.includes(route)) {
    return null;
  }

  // Children prop overrides context content (gives caller an escape hatch)
  const content = children ?? railContent;

  // No contextual controls for this page → render nothing at all (no stray
  // "CONTROLS" strip/toggle). The rail only exists where a page injects filters.
  if (!content) {
    return null;
  }

  const width = isOpen ? LEFT_RAIL_WIDTH_OPEN : LEFT_RAIL_WIDTH_COLLAPSED;

  // On mobile, an OPEN rail must overlay (fixed) rather than push content and
  // cause horizontal overflow. Collapsed it stays a 40px sticky strip (toggle).
  const mobileOpen = isMobile && isOpen;

  // On mobile when collapsed: previously rendered a floating "Filters & legend"
  // FAB that confused users on pages without filters (e.g. /digest). Since
  // the early-return above (line 107) guarantees content exists, we always
  // show the rail toggle on mobile collapsed, but only as the sticky 40px
  // strip aside — same as desktop. The page must really need filters.

  const asideStyle = mobileOpen
    ? {
        position: 'fixed', top: 56, left: 0, bottom: 0,
        width: LEFT_RAIL_WIDTH_OPEN, maxWidth: '85vw',
        zIndex: theme.zIndex.drawer + 2,
        overflow: 'hidden', background: '#ffffff',
        borderRight: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '8px 0 24px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
      }
    : {
        flexShrink: 0, overflow: 'hidden', background: '#ffffff',
        borderRight: '1px solid rgba(0,0,0,0.06)',
        height: 'calc(100vh - 72px)', position: 'sticky', top: 72,
        display: 'flex', flexDirection: 'column',
      };

  return (
    <>
    {mobileOpen && (
      <Box onClick={toggle} sx={{ position: 'fixed', inset: 0, top: 56, bgcolor: 'rgba(0,0,0,0.32)', zIndex: (t) => t.zIndex.drawer + 1 }} />
    )}
    <motion.aside
      animate={{ width: mobileOpen ? LEFT_RAIL_WIDTH_OPEN : width }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={asideStyle}
    >
      {/* Header / toggle. Just the chevron — no "CONTROLS" header, since the
          page that injects content into the rail decides its own heading. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: isOpen ? 0.5 : 0,
          py: 0.5,
          minHeight: 32,
        }}
      >
        <Tooltip title={isOpen ? 'Collapse panel' : 'Expand panel'} placement="right">
          <IconButton size="small" onClick={toggle} aria-label="toggle left rail">
            {isOpen ? (
              <ChevronLeftIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="open"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              style={{ padding: 12 }}
            >
              {content ?? (
                <Box
                  sx={{
                    p: 2,
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                  }}
                >
                  {route} controls — coming soon
                </Box>
              )}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </Box>
    </motion.aside>
    </>
  );
}
